<template>
    <div class="idea-solution-card border border-grey-40 bg-white p-4"
         :class="{'!bg-grey-10': solutionBasic.addedReport}"
         :data-patent-id="solutionBasic.patent_id">
        <div v-if="solutionLoading">
            <div class="th-skeleton mb-4 h-5 w-full"></div>
            <div class="th-skeleton mb-4 h-20 w-full"></div>
            <div class="th-skeleton mb-4 h-20 w-full"></div>
            <div class="th-skeleton mb-4 h-5 w-full"></div>
        </div>

        <div v-else-if="solutionProfile" class="flex">
            <div class="mr-2 mt-0.5 shrink-0 text-sm text-grey-450">
                {{ solutionOrder }}
            </div>
            <div class="grow">
                <div class="flex items-start gap-1 text-sm leading-6 text-black">

                    <SimilarTag v-if="solutionProfile.similarity"
                                :label="$t('similarity')"
                                :similarity="solutionProfile.similarity"
                                class="shrink-0"/>

                    <span v-if="solutionProfile.manualAddIntoResult" class="th-badge h-6 shrink-0">{{ $t('addManually') }}</span>
                    <span class="grow">
                        <router-link :to="`/detail/${solutionProfile.patent_id}/summary`" class=" hover:text-eureka-600 hover:underline">
                            <span v-html="solutionProfile.title || $t('noTitle')">
                            </span>
                        </router-link>
                        <div class="th-badge th-badge-secondary ml-2">{{ solutionProfile.pn }}</div>
                        <LegalStatus :legal_status="solutionProfile.legal_status" class="ml-2"/>
                    </span>
                    <button class="add-report-btn th-btn th-btn-text th-btn-sm invisible" :class="{'!visible':solutionBasic.addedReport}" @click="()=>handleIntoReport(solutionProfile)">
                        <IconCheck class="th-btn-icon" v-if="solutionBasic.addedReport"/>
                        <IconAdd class="th-btn-icon"  v-else/>
                        <span>{{solutionBasic.addedReport? $t('addedReport') : $t('addIntoReport') }}</span>
                    </button>
                </div>
                <div class="mt-2 bg-eureka-500 bg-opacity-[0.04] px-3 py-5 text-sm text-grey-450"  :class="{'opacity-80':solutionBasic.addedReport}" v-if="solutionProfile.efficacy || solutionProfile.means">
                    <p class="mb-3 flex" v-if="solutionProfile.efficacy">
                        <span :class="{'min-w-[120px]':isEnLang}" class="mr-3 shrink-0">【{{ $t('similarEfficacy') }}】</span>
                        <span class="line-clamp-3 flex-wrap" :title="getContentInnerText(solutionProfile.efficacy)" v-html="solutionProfile.efficacy"></span>
                    </p>
                    <p class="flex" v-if="solutionProfile.means">
                        <span :class="{'min-w-[120px]':isEnLang}" class="mr-3 shrink-0">【{{ $t('similarMeans') }}】</span>
                        <span class="line-clamp-3 flex-wrap" :title="getContentInnerText(solutionProfile.means)" v-html="solutionProfile.means"></span>
                    </p>
                </div>
                <slot name="image-row"/>
                <SolutionCardFooter :profile="solutionProfile"/>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import IconAdd from '@/components/icons/IconAdd.vue'
import IconCheck from '@/components/icons/IconCheck.vue'
import SolutionCardFooter from '@/components/result/SolutionCardFooter.vue'
import { ideaResult } from '@/services/result-service'
import { onMounted, ref } from 'vue';
import type { IBaseSolution } from '@/types';
import { LegalStatus } from '@eureka/shared/legal-status'
import SimilarTag from '@/components/SimilarTag.vue'

import { useGlobalState } from '@/composables/use-global-store'
import { useI18n } from 'vue-i18n';
import { getContentInnerText } from '@/infrastructure/utils/replace-htlml'
const { locale } = useI18n()
const isEnLang = locale.value === 'en'
const props = defineProps<{
    solutionBasic: IBaseSolution,
    solutionOrder: number
}>()

const solutionProfile = ref<Nullable<IBaseSolution>>(null)
const solutionLoading = ref<boolean>(false)

onMounted(() => {
    if (props.solutionBasic.title) {
        solutionProfile.value = props.solutionBasic
        return
    }
    solutionLoading.value = true;
    const { editContent } = useGlobalState();
    ideaResult.value.getSolutionProfile(props.solutionBasic, editContent).then(res => {
        solutionProfile.value = res
    }).finally(() => {
        solutionLoading.value = false;
    })
})

const emits = defineEmits<{
    (e: 'toggle:intoReportList', value:IBaseSolution): void
}>()

function handleIntoReport(solution: Nullable<IBaseSolution>) {
    if (!solution) return
    emits('toggle:intoReportList', solution)
}

</script>

<style lang="scss">
.idea-solution-card {
    &:hover {
        @apply border-eureka-500;
        box-shadow: 0px 3px 8px rgba(0, 81, 252, 0.14), 0px 0px 4px rgba(0, 81, 252, 0.08);
        .add-report-btn {
            @apply visible
        }
    }
}
</style>
